import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, Spinner, Callout, Badge } from "@/design/components";
import { ApiError } from "@/api/client";
import { getOperatorMetrics } from "@/api/operator/orgs";
import { getOperatorObservability } from "@/api/operator/observability";
import styles from "./OperatorOverviewScreen.module.css";

const apiMessage = (err: unknown, fallback: string): string => (err instanceof ApiError ? err.message : fallback);
const gbp = (n: number): string =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);

/**
 * Operator Overview — the platform-wide dashboard: org/MRR metrics + system health
 * (event queue, dead events, relay-erasure backlog, relay reachability). MRR is
 * estimated (tier list price × active subscriptions), labelled as such.
 */
export function OperatorOverviewScreen() {
  const metrics = useQuery({ queryKey: ["operator", "metrics"], queryFn: ({ signal }) => getOperatorMetrics(signal) });
  const obs = useQuery({ queryKey: ["operator", "observability"], queryFn: ({ signal }) => getOperatorObservability(signal) });

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <p className="u-eyebrow">Platform</p>
        <h1 className={styles.h1}>Overview</h1>
      </header>

      {metrics.isPending && <Loading label="Loading metrics…" />}
      {metrics.isError && (
        <Callout tone="danger" title="Couldn't load metrics">{apiMessage(metrics.error, "Please try again shortly.")}</Callout>
      )}
      {metrics.data && (
        <div className={styles.tiles}>
          <Tile label="Organizations" value={String(metrics.data.orgs.total)} hint={`${metrics.data.orgs.active} active · ${metrics.data.orgs.suspended} suspended`} />
          <Tile label="Estimated MRR" value={gbp(metrics.data.mrrGbp)} hint={`${metrics.data.activeSubscriptions} active subscriptions`} />
          <Tile label="Channels" value={String(metrics.data.channels)} />
          <Tile label="Members" value={String(metrics.data.members)} />
        </div>
      )}

      <section className={styles.section}>
        <h2 className={styles.h2}>System health</h2>
        {obs.isPending && <Loading label="Loading system health…" />}
        {obs.isError && (
          <Callout tone="danger" title="Couldn't load system health">{apiMessage(obs.error, "Please try again shortly.")}</Callout>
        )}
        {obs.data && (
          <Card className={styles.health}>
            <Row label="Relay link"><RelayBadge reachable={obs.data.relay.reachable} /></Row>
            <Row label="Event queue">
              <span className={styles.mono}>
                {obs.data.eventQueue.pending} pending · {obs.data.eventQueue.processing} processing · {obs.data.eventQueue.dead} dead
              </span>
            </Row>
            <Row label="Dead-event archive"><span className={styles.mono}>{obs.data.deadEvents}</span></Row>
            <Row label="Relay-erasure backlog">
              <Badge tone={obs.data.relayErasureBacklog > 0 ? "warning" : "neutral"}>{obs.data.relayErasureBacklog}</Badge>
            </Row>
          </Card>
        )}
      </section>
    </div>
  );
}

function Loading({ label }: { label: string }) {
  return (
    <div className={styles.loading}>
      <Spinner size={20} color="var(--accent)" /> <span>{label}</span>
    </div>
  );
}

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card className={styles.tile}>
      <span className={styles.tileValue}>{value}</span>
      <span className={styles.tileLabel}>{label}</span>
      {hint && <span className={styles.tileHint}>{hint}</span>}
    </Card>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span>{children}</span>
    </div>
  );
}

function RelayBadge({ reachable }: { reachable: boolean | null }) {
  if (reachable === null) return <Badge tone="neutral">Not monitored</Badge>;
  return reachable ? <Badge tone="success">Reachable</Badge> : <Badge tone="danger">Unreachable</Badge>;
}
